import type { User } from '@frontend/types';

const ECOSYSTEM_ADMIN_GROUP_PREFIX = 'portal-ecosystem-admin::';
const ECOSYSTEM_ADMINS_SUFFIX = '-admins';

/** Ecosystem ids the user may administer (JWT scopes + realm groups). */
export function parseEcosystemAdminScopes(user: User | null | undefined): Set<string> {
  const set = new Set<string>();
  if (!user) return set;

  if (user.allowAllEcosystemAdmin) {
    return set;
  }

  for (const id of user.ecosystemAdminScopes || []) {
    const trimmed = String(id || '').trim();
    if (trimmed) set.add(trimmed);
  }

  for (const g of user.groups || []) {
    const gs = String(g || '');
    if (gs.startsWith(ECOSYSTEM_ADMIN_GROUP_PREFIX)) {
      set.add(gs.slice(ECOSYSTEM_ADMIN_GROUP_PREFIX.length).trim());
      continue;
    }
    // Provision scripts create `<ecosystem-id>-admins` (dual-recognize with portal prefix).
    if (gs.endsWith(ECOSYSTEM_ADMINS_SUFFIX) && gs !== 'patriot-admins') {
      const ecoId = gs.slice(0, -ECOSYSTEM_ADMINS_SUFFIX.length).trim();
      if (ecoId) set.add(ecoId);
    }
  }
  return set;
}

export function isPatriotAdmin(user: User | null | undefined): boolean {
  return Boolean(user?.groups?.includes('patriot-admins'));
}

export function isRealmAdmin(user: User | null | undefined): boolean {
  return Boolean(user?.groups?.includes('realm-admin'));
}

export function hasEcosystemAdminScope(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.allowAllEcosystemAdmin) return true;
  return parseEcosystemAdminScopes(user).size > 0;
}

/** Create / invite users and assign ecosystems (API enforces scopes). */
export function canManageRealmUsers(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPatriotAdmin(user)) return true;
  return hasEcosystemAdminScope(user);
}

/** @deprecated Use canSeeSystemAdmin / canSeeRealmAdmin */
export function canAccessFullAdminHub(user: User | null | undefined): boolean {
  return isPatriotAdmin(user);
}

/** @deprecated Use canSee*Admin helpers */
export function canAccessAdminHub(user: User | null | undefined): boolean {
  return canSeeSystemAdmin(user) || canSeeRealmAdmin(user) || canSeeEcosystemAdmin(user);
}

export function canSeeSystemAdmin(user: User | null | undefined): boolean {
  return isPatriotAdmin(user);
}

export function canSeeRealmAdmin(user: User | null | undefined): boolean {
  return isPatriotAdmin(user) || isRealmAdmin(user);
}

export function canSeeEcosystemAdmin(user: User | null | undefined): boolean {
  if (!user) return false;
  if (isPatriotAdmin(user)) return true;
  return hasEcosystemAdminScope(user);
}

/** Project Admin rail — pass true when access-admin-scope or create-scope reports manageable projects. */
export function canSeeProjectAdmin(
  user: User | null | undefined,
  hasManageableProjects = false
): boolean {
  if (!user) return false;
  if (isPatriotAdmin(user) || hasEcosystemAdminScope(user)) return true;
  return hasManageableProjects;
}
